import { STORY_CANVAS } from '@erp/shared';
import { Component, lazy, Suspense, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../components/ui/index.js';

// Full-screen image editor for story slides. Wraps react-filerobot-image-editor
// (crop / rotate / text / stickers / filters / finetune / resize) and ALWAYS
// exports at the 9:16 story canvas (1080x1920). It is lazy-loaded and wrapped in
// an error boundary + Suspense so a load/runtime failure never breaks the page —
// the caller can still upload the original image unedited.

const LazyFilerobot = lazy(async () => {
  const mod = (await import('react-filerobot-image-editor')) as unknown as {
    default?: unknown;
  };
  return { default: (mod.default ?? mod) as never };
});

/** Cover-fit a source canvas/image into the story canvas and return a JPEG blob. */
async function exportToStoryCanvas(src: HTMLCanvasElement | HTMLImageElement): Promise<Blob> {
  const { width: W, height: H } = STORY_CANVAS;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, W, H);
  const sw = 'width' in src ? (src as HTMLCanvasElement).width || (src as HTMLImageElement).naturalWidth : 0;
  const sh = 'height' in src ? (src as HTMLCanvasElement).height || (src as HTMLImageElement).naturalHeight : 0;
  const scale = Math.max(W / sw, H / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  ctx.drawImage(src, (W - dw) / 2, (H - dh) / 2, dw, dh);
  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.9),
  );
}

interface EditorProps {
  open: boolean;
  sourceUrl: string;
  onSave: (blob: Blob) => void;
  onClose: () => void;
}

interface BoundaryProps { children: ReactNode; onError: () => void }
class EditorBoundary extends Component<BoundaryProps, { failed: boolean }> {
  constructor(props: BoundaryProps) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError(): { failed: boolean } { return { failed: true }; }
  override componentDidCatch(): void { this.props.onError(); }
  override render(): ReactNode { return this.state.failed ? null : this.props.children; }
}

export function ImageEditorDialog({ open, sourceUrl, onSave, onClose }: EditorProps): JSX.Element | null {
  if (!open) return null;

  const handleSave = async (edited: { imageCanvas?: HTMLCanvasElement; imageBase64?: string }): Promise<void> => {
    try {
      let src: HTMLCanvasElement | HTMLImageElement | undefined = edited.imageCanvas;
      if (!src && edited.imageBase64) {
        src = await new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = edited.imageBase64!;
        });
      }
      if (!src) { onClose(); return; }
      const blob = await exportToStoryCanvas(src);
      onSave(blob);
    } catch {
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black">
      <EditorBoundary onError={onClose}>
        <Suspense
          fallback={
            <div className="grid h-full w-full place-items-center text-white">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
              </div>
            </div>
          }
        >
          <div className="h-full w-full">
            {(() => {
              const F = LazyFilerobot as unknown as (props: Record<string, unknown>) => JSX.Element;
              return (
                <F
                  source={sourceUrl}
                  onSave={handleSave}
                  onClose={onClose}
                  Crop={{ ratio: STORY_CANVAS.width / STORY_CANVAS.height, presetsItems: [{ titleKey: 'Story 9:16', ratio: STORY_CANVAS.width / STORY_CANVAS.height }] }}
                  savingPixelRatio={1}
                  previewPixelRatio={1}
                  useBackendTranslations={false}
                />
              );
            })()}
          </div>
        </Suspense>
      </EditorBoundary>
    </div>,
    document.body,
  );
}
